<script setup lang="ts">
  import { ref, useCssModule, useSlots, watch } from 'vue';

  import Expand from '../Expand/Expand.vue';
  import type { IconName } from '../Icon/Icon.types';
  import Icon from '../Icon/Icon.vue';

  export interface AccordionProps {
    /**
     * The filename of the icon that will be displayed on the left side of the header
     */
    icon?: IconName;

    /**
     * Title of the Accordion, displayed in the header
     */
    title?: string;

    /**
     * Whether the Accordion is expanded or not
     */
    isExpanded?: boolean;
  }

  type ToggleState = {
    isExpanded: boolean;
  };

  const props = withDefaults(defineProps<AccordionProps>(), {
    icon: 'circle-info',
    title: undefined,
    isExpanded: false,
  });
  const slots = useSlots();

  const emit = defineEmits<{
    (e: 'toggle', state: ToggleState): void;
    (e: 'open' | 'close'): void;
  }>();

  const classes = useCssModule();

  if (!props.title && !slots.header) {
    throw new Error('Either a title prop or header slot must be provided');
  }

  const localIsExpanded = ref(props.isExpanded);

  function emitEvents() {
    emit('toggle', { isExpanded: localIsExpanded.value });
    emit(localIsExpanded.value ? 'open' : 'close');
  }

  const toggle = () => {
    localIsExpanded.value = !localIsExpanded.value;
    emitEvents();
  };

  watch(
    () => props.isExpanded,
    (newVal) => {
      localIsExpanded.value = newVal;
      emitEvents();
    },
  );
</script>

<template>
  <div class="stash-accordion rounded border border-ice-500 bg-white" data-test="stash-accordion">
    <div
      tabindex="0"
      data-test="stash-accordion|toggle"
      class="stash-accordion__toggle flex items-center"
      :class="[classes.toggle, localIsExpanded && classes['toggle--open']]"
      @click="toggle"
      @keypress.enter="toggle"
      @keypress.space.prevent="toggle"
    >
      <div v-if="slots.header">
        <!-- @slot Accordion header -->
        <slot name="header"> </slot>
      </div>
      <template v-else>
        <Icon class="text-blue-500" size="dense" :name="props.icon" data-test="stash-accordion|toggle-icon" />
        <div class="flex-1 select-none font-medium leading-5 text-ice-900">
          {{ props.title }}
        </div>
      </template>

      <Icon
        class="ml-auto justify-center text-ice-900 transition-transform duration-300"
        size="dense"
        name="chevron-down"
        data-test="stash-accordion|toggle-chevron"
        :class="{ 'rotate-180': localIsExpanded }"
      />
    </div>
    <hr v-show="localIsExpanded" class="mx-[18px] border-ice-200" />
    <Expand :is-expanded="localIsExpanded">
      <div class="stash-accordion__content p-[18px]" data-test="stash-accordion|content">
        <!-- @slot Accordion content -->
        <slot></slot>
      </div>
    </Expand>
  </div>
</template>

<style module>
  @reference "../../../styles/main.css";

  .toggle {
    @apply outline-none ring-blue-500/15;

    display: flex;
    cursor: pointer;
    gap: --spacing(1.5);
    padding: 17px;
    outline: none;
    border-radius: var(--radius-sm);

    &:focus-visible {
      @apply ring-4;
      border-color: var(--color-ice-900);
      padding: 15px;
      border-width: 2px;
    }

    @media (hover: hover) {
      &:hover {
        background-color: var(--color-ice-100);
      }
    }

    &:active {
      background-color: var(--color-ice-200);
    }

    &:active + hr,
    &:hover + hr {
      opacity: 0;
    }
  }

  .toggle--open {
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }
</style>
